<template>
  <div class="favorite-page">
    <div class="page-header">
      <h1 class="page-title">我的收藏</h1>
      <span class="favorite-count">共 {{ favoriteProducts.length }} 件商品</span>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>加载收藏列表中...</p>
    </div>

    <div v-else-if="!isLoggedIn" class="not-logged-in">
      <div class="empty-icon" aria-hidden="true"></div>
      <p class="empty-text">请先登录查看收藏列表</p>
      <router-link to="/login" class="btn btn-primary">去登录</router-link>
    </div>

    <div v-else-if="favoriteProducts.length === 0" class="empty-favorites">
      <div class="empty-icon" aria-hidden="true"></div>
      <p class="empty-text">您还没有收藏任何商品</p>
      <router-link to="/" class="btn btn-primary">去逛逛</router-link>
    </div>

    <div v-else class="favorite-container">
      <div class="toolbar">
        <label class="checkbox-label select-all">
          <input type="checkbox" id="selectAll" v-model="isAllSelected" @change="toggleAllSelection">
          <span class="checkbox-custom"></span>
          <span class="label-text">全选</span>
        </label>
        <div class="toolbar-actions">
          <button 
            class="action-btn batch-delete" 
            @click="confirmBatchUnfavorite"
            :disabled="selectedItems.length === 0"
            :class="{ 'disabled': selectedItems.length === 0 }"
          >
            <i class="icon" aria-hidden="true"></i> 取消选中收藏
          </button>
        </div>
      </div>

      <div class="products-grid">
        <div 
          v-for="favorite in favoriteProducts" 
          :key="favorite.id" 
          class="product-card"
          :class="{ 'selected': selectedItems.includes(favorite.product.id) }"
        >
          <label class="checkbox-label item-checkbox">
            <input type="checkbox" :value="favorite.product.id" v-model="selectedItems" @change="updateAllSelected">
            <span class="checkbox-custom"></span>
          </label>

          <div class="product-image-wrapper" @click="goToProduct(favorite.product.id)">
            <img 
              :src="getProductImageUrl(favorite.product)" 
              :alt="favorite.product.name" 
              :data-product-id="favorite.product.id"
              class="product-image"
              @error="handleImageError($event)"
            />
          </div>

          <div class="product-info">
            <h3 class="product-name" @click="goToProduct(favorite.product.id)">{{ favorite.product.name }}</h3>
            <div class="product-price">
              <span class="price-symbol">¥</span>
              <span class="price-value">{{ favorite.product.price.toFixed(2) }}</span>
            </div>
            <div class="product-stock" :class="favorite.product.stock > 0 ? 'in-stock' : 'out-of-stock'">
              <span class="stock-dot"></span>
              {{ favorite.product.stock > 0 ? `有货 (${favorite.product.stock}件)` : '暂时缺货' }}
            </div>
            <div class="product-actions">
              <button 
                class="btn btn-outline unfavorite-btn"
                @click="unfavoriteProduct(favorite.id, favorite.product.id)"
              >
                <span>取消收藏</span>
              </button>
              <button 
                class="btn btn-primary add-cart-btn"
                @click="addToCart(favorite.product)"
                v-if="favorite.product.stock > 0"
              >
                <span>加入购物车</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 统一非模态Toast提示 -->
    <div class="toast-container">
      <div 
        v-for="toast in toasts" 
        :key="toast.id" 
        class="toast"
        :class="toast.type"
      >
        <span class="toast-icon">{{ getToastIcon(toast.type) }}</span>
        <span class="toast-message">{{ toast.message }}</span>
        <button class="toast-close" @click="removeToast(toast.id)">×</button>
      </div>
    </div>
    
    <!-- 撤销提示条 -->
    <div v-if="showUndoToast" class="undo-toast">
      <span>已取消收藏</span>
      <button class="undo-btn" @click="undoRemove">撤销</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FavoriteProducts',
  data() {
    return {
      favoriteProducts: [],
      loading: true,
      selectedItems: [],
      isAllSelected: false,
      // Toast提示相关
      toasts: [],
      // 撤销删除功能
      deletedFavorites: [],
      showUndoToast: false,
      undoTimer: null
    }
  },
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem('customerToken');
    }
  },
  created() {
    this.loadFavoriteProducts();
  },
  methods: {
    getProductImageUrl(product) {
      if (!product.imageUrl) {
        return `https://picsum.photos/seed/product-${product.id}/200/200.jpg`;
      }
      if (!product.imageUrl.startsWith('http')) {
        return product.imageUrl;
      }
      if (product.imageUrl.includes('huaban.com')) {
        return `https://picsum.photos/seed/product-${product.id}/200/200.jpg`;
      }
      return product.imageUrl;
    },

    handleImageError(e) {
      const productId = e.target.dataset.productId || 'default';
      const defaultImage = `https://picsum.photos/seed/product-${productId}/200/200.jpg`;
      if (e.target.src !== defaultImage) {
        e.target.src = defaultImage;
      }
    },

    goToProduct(productId) {
      this.$router.push(`/product/${productId}`);
    },

    async loadFavoriteProducts() {
      if (!this.isLoggedIn) {
        this.loading = false;
        return;
      }

      try {
        this.loading = true;
        const username = localStorage.getItem('customerUsername');
        const response = await this.$axios.get(`/favorites/${username}`);
        this.favoriteProducts = response.data;
      } catch (err) {
        console.error('加载收藏列表失败:', err);
        this.showToast('加载收藏列表失败，请稍后重试', 'error');
      } finally {
        this.loading = false;
      }
    },

    async unfavoriteProduct(favoriteId, productId) {
      try {
        const username = localStorage.getItem('customerUsername');
        const favoriteToRemove = this.favoriteProducts.find(f => f.id === favoriteId);
        
        await this.$axios.delete(`/favorites/${username}/${productId}`);
        this.favoriteProducts = this.favoriteProducts.filter(favorite => favorite.id !== favoriteId);
        this.selectedItems = this.selectedItems.filter(id => id !== productId);
        this.updateAllSelected();
        
        // 保存删除的收藏用于撤销
        if (favoriteToRemove) {
          this.deletedFavorites.push(favoriteToRemove);
          this.showUndoToast = true;
          
          // 清除之前的定时器
          if (this.undoTimer) {
            clearTimeout(this.undoTimer);
          }
          
          // 3秒后隐藏撤销提示
          this.undoTimer = setTimeout(() => {
            this.showUndoToast = false;
            this.deletedFavorites = [];
          }, 3000);
        }
        
        this.showToast('取消收藏成功', 'success');
      } catch (err) {
        console.error('取消收藏失败:', err);
        this.showToast('取消收藏失败，请稍后重试', 'error');
      }
    },

    async addToCart(product) {
      try {
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        await this.$axios.post('/cart/add', {
          productId: product.id,
          quantity: 1
        }, { headers: { 'X-Username': encodedUsername } });
        this.showToast('已加入购物车', 'success');
      } catch (err) {
        console.error('加入购物车失败:', err);
        this.showToast('加入购物车失败，请稍后重试', 'error');
      }
    },

    toggleAllSelection() {
      if (this.isAllSelected) {
        this.selectedItems = this.favoriteProducts.map(f => f.product.id);
      } else {
        this.selectedItems = [];
      }
    },

    updateAllSelected() {
      this.isAllSelected = this.selectedItems.length === this.favoriteProducts.length;
    },

    confirmBatchUnfavorite() {
      if (this.selectedItems.length === 0) {
        this.showToast('请先选择要取消收藏的商品', 'warning');
        return;
      }

      if (confirm(`确定要取消选中的 ${this.selectedItems.length} 件商品的收藏吗？`)) {
        this.batchUnfavorite();
      }
    },

    async batchUnfavorite() {
      try {
        const username = localStorage.getItem('customerUsername');
        let successCount = 0;

        for (const productId of this.selectedItems) {
          try {
            await this.$axios.delete(`/favorites/${username}/${productId}`);
            successCount++;
          } catch (err) {
            console.error(`取消收藏商品 ${productId} 失败:`, err);
          }
        }

        this.favoriteProducts = this.favoriteProducts.filter(
          f => !this.selectedItems.includes(f.product.id)
        );
        this.selectedItems = [];
        this.isAllSelected = false;

        this.showToast(`成功取消 ${successCount} 件商品的收藏`, 'success');
      } catch (err) {
        console.error('批量取消收藏失败:', err);
        this.showToast('批量取消收藏失败，请稍后重试', 'error');
      }
    },

    showToast(message, type = 'info', duration = 3000) {
      const toast = {
        id: Date.now() + Math.random(),
        message,
        type
      };
      this.toasts.push(toast);
      
      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(toast.id);
        }, duration);
      }
    },
    
    showError(msg) {
      this.showToast(msg, 'error');
    },
    
    showSuccess(msg) {
      this.showToast(msg, 'success');
    },
    
    showInfo(msg) {
      this.showToast(msg, 'info');
    },
    
    showWarning(msg) {
      this.showToast(msg, 'warning');
    },
    
    removeToast(id) {
      const index = this.toasts.findIndex(toast => toast.id === id);
      if (index !== -1) {
        this.toasts.splice(index, 1);
      }
    },
    
    getToastIcon(type) {
      const icons = {
        success: '✓',
        error: '✕',
        info: 'ℹ',
        warning: '!'
      };
      return icons[type] || 'ℹ';
    },
    
    async undoRemove() {
      if (this.deletedFavorites.length === 0) return;
      
      try {
        const username = localStorage.getItem('customerUsername');
        
        // 重新添加所有删除的收藏
        for (const favorite of this.deletedFavorites) {
          await this.$axios.post(`/favorites/${username}/${favorite.product.id}`);
        }
        
        // 重新获取收藏列表
        await this.loadFavoriteProducts();
        
        // 清除撤销状态
        this.showUndoToast = false;
        this.deletedFavorites = [];
        if (this.undoTimer) {
          clearTimeout(this.undoTimer);
          this.undoTimer = null;
        }
        
        this.showToast('已恢复收藏', 'success');
      } catch (error) {
        console.error('撤销删除失败:', error);
        this.showToast('撤销失败，请稍后重试', 'error');
      }
    }
  }
}
</script>

<style scoped>
.favorite-page {
  min-height: calc(100vh - 80px);
  background-color: #f5f5f5;
  padding: 20px 0;
}

.page-header {
  max-width: 1200px;
  margin: 0 auto 20px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.favorite-count {
  font-size: 14px;
  color: #999;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #ff4400;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.not-logged-in,
.empty-favorites {
  max-width: 400px;
  margin: 40px auto;
  text-align: center;
  padding: 60px 20px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.empty-icon {
  position: relative;
  width: 72px;
  height: 72px;
  font-size: 0;
  margin-bottom: 20px;
  margin-inline: auto;
  border-radius: 24px;
  background: linear-gradient(145deg, #ffffff, #e9edf5);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
}

.not-logged-in .empty-icon::before,
.not-logged-in .empty-icon::after,
.empty-favorites .empty-icon::before,
.empty-favorites .empty-icon::after {
  content: '';
  position: absolute;
}

.not-logged-in .empty-icon::before {
  top: 17px;
  left: 24px;
  width: 24px;
  height: 16px;
  border: 2px solid #111827;
  border-bottom: 0;
  border-radius: 14px 14px 0 0;
}

.not-logged-in .empty-icon::after {
  top: 30px;
  left: 19px;
  width: 34px;
  height: 24px;
  border: 2px solid #111827;
  border-radius: 8px;
}

.empty-favorites .empty-icon::before,
.empty-favorites .empty-icon::after {
  top: 20px;
  width: 18px;
  height: 30px;
  background: #111827;
  border-radius: 18px 18px 0 0;
}

.empty-favorites .empty-icon::before {
  left: 19px;
  transform: rotate(-45deg);
  transform-origin: bottom right;
}

.empty-favorites .empty-icon::after {
  right: 19px;
  transform: rotate(45deg);
  transform-origin: bottom left;
}

.empty-text {
  font-size: 16px;
  color: #999;
  margin-bottom: 25px;
}

.favorite-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: white;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.checkbox-label input {
  display: none;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-radius: 4px;
  position: relative;
  margin-right: 8px;
  transition: all 0.2s ease;
}

.checkbox-label input:checked + .checkbox-custom {
  background-color: #ff4400;
  border-color: #ff4400;
}

.checkbox-label input:checked + .checkbox-custom::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.select-all .label-text {
  font-size: 14px;
  color: #333;
}

.toolbar-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 8px 16px;
  font-size: 14px;
  color: #666;
  background-color: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
}

.action-btn .icon {
  position: relative;
  width: 18px;
  height: 18px;
  font-size: 0;
  flex: 0 0 18px;
  display: inline-flex;
}

.action-btn .icon::before,
.action-btn .icon::after {
  content: '';
  position: absolute;
}

.action-btn .icon::before {
  inset: 5px 3px 2px;
  border: 2px solid currentColor;
  border-top: 0;
  border-radius: 0 0 4px 4px;
}

.action-btn .icon::after {
  top: 2px;
  left: 4px;
  width: 10px;
  height: 2px;
  background: currentColor;
  box-shadow: 2px -3px 0 -1px currentColor;
}

.action-btn:hover:not(.disabled) {
  background-color: #f5f5f5;
  color: #ff4400;
}

.action-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.product-card {
  position: relative;
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
}

.product-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.product-card.selected {
  border: 2px solid #ff4400;
  background-color: #fff8f5;
}

.item-checkbox {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 10;
}

.product-image-wrapper {
  width: 100%;
  height: 200px;
  overflow: hidden;
  cursor: pointer;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card:hover .product-image {
  transform: scale(1.05);
}

.product-info {
  padding: 16px;
}

.product-name {
  font-size: 15px;
  color: #333;
  margin: 0 0 10px 0;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  transition: color 0.2s ease;
}

.product-name:hover {
  color: #ff4400;
}

.product-price {
  display: flex;
  align-items: baseline;
  margin-bottom: 8px;
}

.price-symbol {
  font-size: 14px;
  color: #ff4400;
  font-weight: bold;
}

.price-value {
  font-size: 22px;
  color: #ff4400;
  font-weight: bold;
}

.product-stock {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  margin-bottom: 15px;
}

.product-stock.in-stock {
  color: #26a69a;
}

.product-stock.out-of-stock {
  color: #999;
}

.stock-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.product-stock.in-stock .stock-dot {
  background-color: #26a69a;
}

.product-stock.out-of-stock .stock-dot {
  background-color: #ddd;
}

.product-actions {
  display: flex;
  gap: 10px;
}

.btn {
  flex: 1;
  padding: 10px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border: none;
}

.btn-primary {
  background: linear-gradient(135deg, #ff4400 0%, #ff6a00 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 68, 0, 0.3);
}

.btn-outline {
  background-color: transparent;
  border: 1px solid #ddd;
  color: #666;
}

.btn-outline:hover {
  border-color: #ff4400;
  color: #ff4400;
}

/* 统一非模态Toast提示容器 */
.toast-container {
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toast {
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: toastSlideIn 0.3s ease;
  min-width: 200px;
}

@keyframes toastSlideIn {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.toast-icon {
  font-size: 16px;
  font-weight: bold;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.toast-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.toast-success .toast-icon {
  background-color: #28a745;
  color: white;
}

.toast-error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.toast-error .toast-icon {
  background-color: #dc3545;
  color: white;
}

.toast-warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.toast-warning .toast-icon {
  background-color: #ffc107;
  color: #856404;
}

.toast-info {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.toast-info .toast-icon {
  background-color: #17a2b8;
  color: white;
}

.toast-message {
  flex: 1;
}

.toast-close {
  width: 24px;
  height: 24px;
  border: none;
  background-color: transparent;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-close:hover {
  opacity: 1;
}

@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .products-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .product-card {
    padding-left: 45px;
  }

  .product-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}

/* 撤销提示样式 */
.undo-toast {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  padding: 14px 24px;
  background-color: #333;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: undoToastSlideIn 0.3s ease;
}

@keyframes undoToastSlideIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.undo-btn {
  padding: 6px 16px;
  background-color: #ff4400;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.undo-btn:hover {
  background-color: #ff6a00;
}

.favorite-page,
.favorites-page,
.favorite-products {
  color: var(--color-text);
}

.favorite-container {
  display: grid;
  gap: 1rem;
}

.products-grid .product-card {
  padding: 1rem;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(120px, 180px) minmax(0, 1fr);
  gap: 1rem;
  align-items: stretch;
}

.product-image-wrapper {
  border-radius: 20px;
  overflow: hidden;
  width: 100%;
  min-height: 0;
  aspect-ratio: 1 / 1;
  background: rgba(15, 23, 42, 0.04);
}

.products-grid .product-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-info {
  padding-top: 0.35rem;
  display: grid;
  align-content: center;
  gap: 0.9rem;
}

.product-name {
  margin: 0;
}

.toolbar,
.favorites-toolbar,
.favorite-item,
.toast,
.undo-toast {
  border: 1px solid rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: var(--shadow-soft);
}

.product-card,
.favorite-item {
  border-radius: 26px;
}

.product-actions .btn,
.undo-btn {
  border-radius: 999px;
}

@media (max-width: 900px) {
  .products-grid .product-card {
    grid-template-columns: 1fr;
  }

  .products-grid .product-image-wrapper {
    max-width: 220px;
    justify-self: center;
  }
}

.favorite-page {
  background:
    radial-gradient(circle at top left, rgba(226, 232, 240, 0.65), transparent 36%),
    linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
}

.product-card,
.toolbar,
.undo-toast,
.toast {
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.products-grid .product-card {
  position: relative;
  animation: favoriteCardRise 0.55s ease both;
}

.products-grid .product-card:nth-child(2) {
  animation-delay: 0.05s;
}

.products-grid .product-card:nth-child(3) {
  animation-delay: 0.1s;
}

.products-grid .product-card:nth-child(4) {
  animation-delay: 0.15s;
}

.product-image {
  transition: transform 0.4s ease, filter 0.4s ease;
}

.products-grid .product-card:hover {
  transform: scale(1.015) translateY(-2px);
  border-color: rgba(0, 113, 227, 0.18);
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.1);
}

.products-grid .product-card:hover .product-image {
  transform: scale(1.07);
  filter: saturate(1.05);
}

.product-actions .btn,
.action-btn {
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    background-color 0.22s ease,
    border-color 0.22s ease;
}

.product-actions .btn:hover,
.action-btn:hover:not(.disabled) {
  transform: translateY(-2px);
}

@keyframes favoriteCardRise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
